package modysy

import modysy.datastructures.Vector2
import modysy.gamestate.{GameState, GameStateManager}
import modysy.menu._
import modysy.model.{Agent, GameObject}
import modysy.simulation.collision.{
  CollisionEngine,
  CollisionPenetrationResolver,
  PortalResolver,
  SpatialHashManager
}
import modysy.simulation.events.coin
import modysy.simulation.moveObjects
import modysy.simulation.pp.SuccessConditions.LevelResult
import modysy.simulation.pp.SuccessConditions.LevelResult.LevelResult
import modysy.tools._
import modysy.view.Render
import org.scalajs.dom
import org.scalajs.dom.ext.{Ajax, KeyCode}

import scala.collection.mutable
import scala.collection.mutable.ListBuffer
import scala.concurrent.ExecutionContext.Implicits.global
import scala.scalajs.js
import scala.util.{Failure, Success}

/** Links all game parts together. An instance is created by the main function */
class GameController() {
  private val WIDTH_LEVEL = 1920
  private val HEIGHT_LEVEL = 1010
  private var raf = 0
  private val agentCount = 7

  private val objectsToAdd = new mutable.ListBuffer[GameObject]()

  // TODO i don't think this is the best way to do this
  //private var loaded: Boolean = true

  private val fixedTargetFPS: Double = 60d
  private val updatePeriod: Double = 1000d / fixedTargetFPS

  private val render: Render = new Render(WIDTH_LEVEL, HEIGHT_LEVEL)

  // optimal cell size should be in relation to agent size
  val collisionEngine: CollisionEngine =
    new SpatialHashManager(WIDTH_LEVEL, HEIGHT_LEVEL, 50) // TODO optimise cell size

  (1 to 10).foreach(
    number =>
      Ajax
        .get(s"./data/level$number.json")
        .onComplete(_ match {
          case Success(r) =>
            levelList(number - 1) = JsonParser.deserialize(r.responseText)
          case Failure(r) => println(r.getMessage)
        })
  )

  private val gameStateManager = new GameStateManager(this)
  private val levelList = new Array[Level](10)

  private var level: Level = new Level1(WIDTH_LEVEL, HEIGHT_LEVEL)

  private var gameResult: LevelResult = LevelResult.undecided

  private val inputHandler: InputHandler =
    new InputHandler(this, this.render) // TODO gameController is not needed for anything

  private var winScreenMenu: WinScreenMenu =
    new WinScreenMenu(
      gameStateManager,
      inputHandler,
      render.canvas.width,
      render.canvas.height
    )
  private var loseScreenMenu: LoseScreenMenu =
    new LoseScreenMenu(
      gameStateManager,
      inputHandler,
      render.canvas.width,
      render.canvas.height
    )
  private var mainMenu: MainMenu =
    new MainMenu(
      gameStateManager,
      inputHandler,
      render.canvas.width,
      render.canvas.height
    )
  private var _pauseMenu: PauseMenu =
    new PauseMenu(
      gameStateManager,
      inputHandler,
      render.canvas.width,
      render.canvas.height
    )

  def pauseMenu: PauseMenu = _pauseMenu

  private var levelMenu: LevelMenu =
    new LevelMenu(
      gameStateManager,
      inputHandler,
      render.canvas.width,
      render.canvas.height,
      levelList.length
    )

  private var ingameMenu: IngameMenu =
    new IngameMenu(
      gameStateManager,
      inputHandler,
      render.canvas.width,
      render.canvas.height
    )

  def updateMenus() = {
    winScreenMenu = new WinScreenMenu(
      gameStateManager,
      inputHandler,
      render.canvas.width,
      render.canvas.height
    )
    loseScreenMenu = new LoseScreenMenu(
      gameStateManager,
      inputHandler,
      render.canvas.width,
      render.canvas.height
    )
    mainMenu = new MainMenu(
      gameStateManager,
      inputHandler,
      render.canvas.width,
      render.canvas.height
    )
    _pauseMenu = new PauseMenu(
      gameStateManager,
      inputHandler,
      render.canvas.width,
      render.canvas.height
    )

    ingameMenu = new IngameMenu(
      gameStateManager,
      inputHandler,
      render.canvas.width,
      render.canvas.height
    )

    def pauseMenu: PauseMenu = _pauseMenu

    levelMenu = new LevelMenu(
      gameStateManager,
      inputHandler,
      render.canvas.width,
      render.canvas.height,
      levelList.length
    )
  }

  private val energyStorage: EnergyStorage = new EnergyStorage

  //Sound
  val bgm_player = new AudioPlayer
  val sfx_player = new AudioPlayer
  gameStateManager.enterMainMenu()

  private val toolbox: Toolbox = new Toolbox(energyStorage)
  private var obstaclePlacementTool =
    new ObstaclePlacementTool(this)
  inputHandler.bindOnClickEvents(
    (mousePosCanvas: Vector2, _: GameController) =>
      toolbox.onMousePrimaryButtonClick(
        mousePosCanvas,
        render.canvasToWorldMenu(mousePosCanvas)
    )
  )
  inputHandler.bindOnMoveEvents(
    (mousePosCanvas: Vector2, _: GameController) =>
      toolbox.onMouseMove(render.canvasToWorld(mousePosCanvas))
  )

  inputHandler.bindOnTouchStartEvents((e: dom.TouchEvent) => {
    if (gameStateManager.gameState == GameState.ingamePlaying) {
      if (e.touches.length == 1) { // only one finger gestures can be toolbox interactions
        val canvasPos = render.screenToCanvas(
          e.changedTouches.item(0).clientX,
          e.changedTouches.item(0).clientY
        )
        val worldPos = render.canvasToWorld(canvasPos)

        var consume = toolbox.onTouchSingleStart(
          canvasPos,
          worldPos,
          render.canvasToWorld(Vector2(render.width / 2, render.height / 2))
        )

        if (!consume) consume = obstaclePlacementTool.onTouchStart(worldPos)
        if (!consume) consume = render.onTouchStart(canvasPos)
      } else if (e.touches.length == 2) {
        val canvasPos = render.screenToCanvas(
          e.changedTouches.item(0).clientX,
          e.changedTouches.item(0).clientY
        )
        val worldPos = render.canvasToWorld(canvasPos)

        obstaclePlacementTool.onTouchStart(worldPos)
        render.onTouchStart(canvasPos)
      }
    }
  })
  inputHandler.bindOnTouchMoveEvents((e: dom.TouchEvent) => {
    if (gameStateManager.gameState == GameState.ingamePlaying) {
      if (e.touches.length == 1) { // only one finger gestures can be toolbox interactions
        val canvasPos = render.screenToCanvas(
          e.changedTouches.item(0).clientX,
          e.changedTouches.item(0).clientY
        )

        var consume =
          toolbox
            .onTouchSingleMove(canvasPos, render.canvasToWorld(canvasPos))

        if (!consume) consume = render.onTouchSingleMove(canvasPos)
      } else if (e.touches.length == 2) {
        val canvasPos1 =
          render.screenToCanvas(e.touches.item(0).clientX,
                                e.touches.item(0).clientY)
        val worldPos1 = render.canvasToWorld(canvasPos1)

        val canvasPos2 =
          render.screenToCanvas(e.touches.item(1).clientX,
                                e.touches.item(1).clientY)
        val worldPos2 = render.canvasToWorld(canvasPos2)

        var consume = obstaclePlacementTool.onTouchMove(worldPos1, worldPos2)

        if (!consume) render.onTouchDoubleMove(canvasPos1, canvasPos2)
      }
    }
  })
  inputHandler.bindOnTouchEndEvents((e: dom.TouchEvent) => {
    if (gameStateManager.gameState == GameState.ingamePlaying) {
      // only one finger gestures can be toolbox interactions
      if (e.touches.length + 1 == 1) { // ended touch is not in touches anymore -> +1
        val canvasPos = render.screenToCanvas(
          e.changedTouches.item(0).clientX,
          e.changedTouches.item(0).clientY
        )
        toolbox.onTouchSingleEnd(canvasPos, render.canvasToWorld(canvasPos))

        render.onTouchEnd()
        obstaclePlacementTool.onTouchEnd(None)
      } else if (e.touches.length + 1 == 2) {
        val canvasPos =
          render.screenToCanvas(e.touches.item(0).clientX,
                                e.touches.item(0).clientY)
        val worldPos = render.canvasToWorld(canvasPos)

        render.onTouchEnd()
        obstaclePlacementTool.onTouchEnd(Some(worldPos))
      }
    }
  })

  /*
  var dialog1 = new Dialog(
    new Vector2(120, 110),
    "This is your Toolbox!",
    180,
    -1,
    "top"
  )

  var dialog2 = new Dialog(
    new Vector2(width - 150, 150),
    "Dialog-Box with cooldown 3s",
    200,
    3000
  )

  val dialogs = new mutable.ListBuffer[Dialog]()
  dialogs += dialog1
  dialogs += dialog2
   */
  inputHandler.bindOnClickEvents(
    (mousePosCanvas: Vector2, _: GameController) =>
      for (dia <- level.dialogs) {
        dia.primaryMouseButtonPressed(
          mousePosCanvas,
          render.canvasToWorld(mousePosCanvas)
        )
    }
  )

  inputHandler.bindOnPressEvents(
    (
        e: dom.KeyboardEvent,
        gameController: GameController
    ) => {
      if (e.key.equals("v")) {
        loadLevel(new LevelEditor(1920, 1010))
      }
    }
  )

  inputHandler.bindOnPressAndMouseEvents(
    (e: dom.KeyboardEvent,
     mouse: Option[Vector2],
     gameController: GameController) => {
      if (isAllDigits(e.key)) {
        toolbox.activateTool(e.key.toInt - 1, render.canvasToWorld(mouse.get))
      }
    }
  )

  def isAllDigits(x: String) = x forall Character.isDigit

  inputHandler.bindOnPressEvents(
    (e: dom.KeyboardEvent, gameController: GameController) => {
      if (e.key == "s") {
        val json = JsonSaver.toJson(level)
        FileSaver.saveAs(
          new dom.Blob(
            scalajs.js.Array[scalajs.js.Any](json.toString()),
            dom.BlobPropertyBag("type: application/json; charset: utf-8")
          ),
          "level.json"
        )
      }
    }
  )

  inputHandler.bindOnPressAndMouseEvents(
    (e: dom.KeyboardEvent,
     mouse: Option[Vector2],
     gameController: GameController) => {
      if (isAllDigits(e.key)) {
        toolbox.activateTool(e.key.toInt - 1, render.canvasToWorld(mouse.get))
      }
    }
  )

  private val _uhr: Clock = new Clock(1)

  def uhr: Clock = _uhr

  private val coinItem: coin = new coin(enable = false)
  inputHandler.bindOnClickEvents(
    (mousePosCanvas: Vector2, _: GameController) =>
      coinItem.checkClick(mousePosCanvas, energyStorage, uhr)
  )

  /** Pause Button **/
  var screenWidthpause = dom.window.innerWidth.toInt
  var screenHeightpause = dom.window.innerHeight.toInt

  var widthpause = screenWidthpause
  var heightpause = (screenWidthpause.toDouble / 1.9001).toInt + 70

  if (heightpause > screenHeightpause) {
    heightpause = screenHeightpause
    widthpause = ((screenHeightpause - 70).toDouble * 1.9001).toInt
  }

  /*val pausebutton: TextButton = new TextButton(
    225,
    60,
    "Pause"
  )
  pausebutton.fontSize = 60
  pausebutton.colorSelected = Color.Green

  pausebutton.action = () => {
    gameStateManager.pauseGame()
  }

  val tmp = pausebutton.isEnabled
  pausebutton.isEnabled = () => {
    tmp() && (gameStateManager.gameState == GameState.ingamePlaying)
    //true
  }
  pausebutton.enableTouch(inputHandler)*/

  /** Start the game */
  def startGameLoop(): Unit = {
    var prevTime = js.Date.now()
    //Uhr.startClock()

    lazy val gameLoop: Double => Unit = { time =>
      val beginTime = js.Date.now()
      level.objects foreach {
        _.onTimePassed((beginTime - prevTime) / 1000.0)
      }
      level.objects ++= objectsToAdd
      objectsToAdd.clear()

      val timeDelta = 1

      gameStateManager.gameState match {
        case GameState.ingamePlaying =>
          // the actual content
          loopRunning(timeDelta)
          menuLoop(ingameMenu)
        case GameState.mainMenu => menuLoop(mainMenu)
        case GameState.endscreenWin =>
          loopRunningReduced(timeDelta)
          //menuLoop(ingameMenu)
          menuLoop(winScreenMenu)
        case GameState.endscreenLose =>
          loopRunningReduced(timeDelta)
          //menuLoop(ingameMenu)
          menuLoop(loseScreenMenu)
        case GameState.ingamePaused =>
          render.render(level.portals
            .flatMap(p => List(p._1, p._2)) ++ level.objects ++ level.innerField.borders)
          render.renderToolbox(toolbox, inputHandler.mouseCanvasPos)
          //render.renderEnergyStorage(energyStorage)
          render.renderClock(_uhr)
          //menuLoop(ingameMenu)
          menuLoop(_pauseMenu)
        case GameState.levelMenu => menuLoop(levelMenu)
        case _                   => nothingLoop()
      }

      prevTime = js.Date.now()
      dom.window.requestAnimationFrame(gameLoop)
    }

    dom.window.requestAnimationFrame(gameLoop)
    //raf = dom.window.requestAnimationFrame(gameLoop)
    //dom.window.setInterval(gameLoop, updatePeriod)
  }

  /** Calculates and renders one simulation step.
    *
    * @param timeDelta multiplier of the needed length of this logic step
    */
  private def loopRunning(timeDelta: Double): Unit = {

    level.objects
      .filter(_.isInstanceOf[Agent])
      .map(_.asInstanceOf[Agent])
      .foreach(ag => {
        if (ag.behaviour_active) ag.state.behaviour(ag, level.objects)
        else ag.body.velocity.linear = 0
      })

    /*Behaviours
      .applyBehaviours(
        level.objects
          .filter(_.isInstanceOf[Agent])
          .map(_.asInstanceOf[Agent]))*/

    val agentsMoving = level.objects.exists(o => {
      var ret: Boolean = false
      if (o.isInstanceOf[Agent]) {
        val a = o.asInstanceOf[Agent]
        ret = (a.state.name == "+L" || a.state.name == "-L") && !a.body.velocity.isZero
      }
      ret
    })

    if (agentsMoving && !bgm_player.playing) {
      bgm_player.resetAudio()
      bgm_player.playAudio()
    } else if (!agentsMoving && bgm_player.playing) {
      bgm_player.pauseAudio()
    }

    collisionEngine.register(level.objects ++ level.innerField.borders)

    moveObjects(level.objects, timeDelta)
    energyStorage.updateAnimation()

    // resolve collisions until nothing left to resolve
    var anyCollisionsResolved = true
    var resolvedCollisions = new mutable.HashSet[(GameObject, GameObject)]()
    while (anyCollisionsResolved) {
      collisionEngine.register(level.objects ++ level.innerField.borders)
      val collisions = collisionEngine
        .calculateCollisions()
        .filter(
          c =>
            !resolvedCollisions
              .contains((c.first, c.second)) && !resolvedCollisions
              .contains((c.second, c.first))
        )
      val resolvedNow = CollisionPenetrationResolver.resolve(collisions)

      resolvedNow
        .filter(p => p._1.isInstanceOf[Agent] && p._2.isInstanceOf[Agent])
        .map(p => (p._1.asInstanceOf[Agent], p._2.asInstanceOf[Agent]))
        .foreach(p => {
          level.protocol.interact(p._1, p._2, energyStorage)
          //this.sfx_player.playAudio()
        })

      resolvedNow.foreach(p => {
        p._1.onCollision(p._2)
        p._2.onCollision(p._1)

      })

      anyCollisionsResolved = resolvedNow.nonEmpty
      resolvedCollisions ++= resolvedNow
    }

    PortalResolver.resolve(level.objects, level.portals)

    if (inputHandler.keysDown.contains(KeyCode.R)) {
      this.level.resetObjects()
    }

    if (inputHandler.keysDown.contains(KeyCode.Q)) {
      obstaclePlacementTool.rotateCCW()
    }
    if (inputHandler.keysDown.contains(KeyCode.E)) {
      obstaclePlacementTool.rotateCW()
    }

    level.objects = level.objects.filter(_.alive)
    render.render(
      level.portals.flatMap(p => List(p._1, p._2)) ++
        level.objects ++ level.innerField.borders)
    render.renderToolbox(toolbox, inputHandler.mouseCanvasPos)

    render.updateZoomScroll(
      inputHandler.keysDown,
      inputHandler.mouseCanvasPos,
      inputHandler.scrollEvent
    )
    inputHandler.scrollEvent = 0

    //render.renderEnergyStorage(energyStorage)
    var dialogs = level.dialogs
    for (d <- dialogs) {
      render.renderDialog(d)
    }
    _uhr.updateTime(true)
    render.renderClock(_uhr)

    //pausebutton.update(inputHandler);
    //render.renderPausebutton(pausebutton);

    if (level.coin == true) {

      coinItem.createNewCoin()
      if (coinItem.existingCoin == true)
        render.renderCoin(coinItem)
      coinItem.updateTime()
    }
    gameResult = level.result
    if (_uhr.timeUp()) {
      //gameResult = LevelResult.lose
      gameResult = level.onTimeUp
    }

    if (gameResult == LevelResult.win) {
      //loaded = false
      inputHandler.clearKeyEvents()
      gameStateManager.winGame()
    } else if (gameResult == LevelResult.lose) {
      //loaded = false
      inputHandler.clearKeyEvents()
      gameStateManager.loseGame()
    }

    if (inputHandler.activeKeyEvent(KeyCode.P)
        || !dom.document.hasFocus()) {
      gameStateManager.pauseGame()
      inputHandler.clearKeyEvents()
    }

  }

  private def loopRunningReduced(timeDelta: Double): Unit = {

    level.objects
      .filter(_.isInstanceOf[Agent])
      .map(_.asInstanceOf[Agent])
      .foreach(ag => {
        if (ag.behaviour_active) ag.state.behaviour(ag, level.objects)
      })

    collisionEngine.register(level.objects ++ level.innerField.borders)

    moveObjects(level.objects, timeDelta)

    // resolve collisions until nothing left to resolve
    var anyCollisionsResolved = true
    var resolvedCollisions = new mutable.HashSet[(GameObject, GameObject)]()
    while (anyCollisionsResolved) {
      val collisions = collisionEngine.calculateCollisions
        .filter(
          c =>
            !resolvedCollisions
              .contains((c.first, c.second)) && !resolvedCollisions
              .contains((c.second, c.first))
        )
      val resolvedNow = CollisionPenetrationResolver.resolve(collisions)

      resolvedNow
        .filter(p => p._1.isInstanceOf[Agent] && p._2.isInstanceOf[Agent])
        .map(p => (p._1.asInstanceOf[Agent], p._2.asInstanceOf[Agent]))
        .foreach(p => level.protocol.interact(p._1, p._2, energyStorage))

      resolvedNow.foreach(p => {
        p._1.onCollision(p._2)
        p._2.onCollision(p._1)
      })

      anyCollisionsResolved = resolvedNow.nonEmpty
      resolvedCollisions ++= resolvedNow
    }

    PortalResolver.resolve(level.objects, level.portals)

    level.objects = level.objects.filter(_.alive)
    render.render(
      level.portals
        .flatMap(p => List(p._1, p._2)) ++ level.objects ++ level.innerField.borders)

  }

  def menuLoop(menu: AbstractMenu): Unit = {
    menu.update()
    render.renderMenu(menu)
  }

  def nothingLoop(): Unit = {
    render.renderNothing()
  }

  def addGameObject(gameObject: GameObject): Unit = {
    objectsToAdd += gameObject
  }

  def getObjects(): ListBuffer[GameObject] = {
    return this.level.objects
  }

  def loadLevel(lv_nr: Int): Unit = {

    this.level = this.levelList(lv_nr - 1)
    level.resetObjects()

    this.level.objects
      .filter(_.isInstanceOf[Agent])
      .map(_.asInstanceOf[Agent])
      .foreach(ag => {

        ag.state

      })

    this.uhr.setTime(level.time)
    this.uhr.startClock()

    resetToolbox()
  }

  def loadLevel(level: Level): Unit = {

    this.level = level
    level.resetObjects()

    this.level.objects
      .filter(_.isInstanceOf[Agent])
      .map(_.asInstanceOf[Agent])
      .foreach(ag => {

        ag.state

      })

    this.uhr.setTime(level.time)
    this.uhr.startClock()

    resetToolbox()
  }

  def resetToolbox(): Unit = {
    toolbox.resetToolbox()
    level.availableTools(this).foreach(toolbox.addTool(_))
    obstaclePlacementTool =
      toolbox.getToolBoxAt(1).asInstanceOf[ObstaclePlacementTool]
  }

  def getCollisionEngine: CollisionEngine = collisionEngine

  def getLevel: Level = level

  def numLevels: Int = levelList.length

  dom.window.onresize = (e: dom.Event) => {
    render.onWindowResize()
    updateMenus()
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/2d4fb5cd/0/mtv-pr-2018/modysy-product/src/main/scala/modysy/GameController.scala